import { audio } from '../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../ui/TouchTarget.js';
import { resolveStoryForestLayout, STORY_FOREST_LAYOUT_HEIGHT, STORY_FOREST_LAYOUT_WIDTH } from '../components/StoryForestLayout.js';
import { STORY_FOREST_ASSETS } from '../StoryForestAssets.js';
import {
  STORY_FOREST_DISTRICTS,
  completeStoryForestDistrictProgress,
  getStoryForestState,
  saveStoryForestDistrictProgress,
  visitStoryForestDistrict,
} from '../StoryForestState.js';
import { CHARACTER_GROVE_ASSETS } from '../minigames/character-grove/CharacterGroveAssets.js';
import { ReadingEvidenceState, type ReadingEvidenceSerializedState } from '../minigames/reading-evidence/ReadingEvidenceState.js';
import {
  MAIN_IDEA_MEADOW_ASSETS,
  MAIN_IDEA_MEADOW_ASSET_LIST,
  canEnterMainIdeaMeadow,
  getMissingMainIdeaMeadowAssets,
} from './MainIdeaMeadowAssets.js';
import { MAIN_IDEA_MEADOW_PASSAGE } from './MainIdeaMeadowConfig.js';
import { canEnterInferenceHollow } from '../inference-hollow/InferenceHollowAssets.js';

export const MAIN_IDEA_MEADOW_SCENE_KEY = 'MainIdeaMeadow';
const FONT_READING = '"Nunito", "Segoe UI", Arial, sans-serif';

interface MainIdeaMeadowProgress extends ReadingEvidenceSerializedState {
  readonly restored: boolean;
}

interface ChoiceView {
  id: string;
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly badge: Phaser.GameObjects.Text;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

export class MainIdeaMeadowScene extends Phaser.Scene {
  static readonly requiredImageKeys = MAIN_IDEA_MEADOW_ASSET_LIST.map(asset => asset.key);

  private state!: ReadingEvidenceState;
  private dormantBackground!: Phaser.GameObjects.Image;
  private restoredBackground!: Phaser.GameObjects.Image;
  private storyPanel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private storyTitle!: Phaser.GameObjects.Text;
  private passageText!: Phaser.GameObjects.Text;
  private pageText!: Phaser.GameObjects.Text;
  private previousText!: Phaser.GameObjects.Text;
  private nextPageText!: Phaser.GameObjects.Text;
  private questionText!: Phaser.GameObjects.Text;
  private phaseText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeText!: Phaser.GameObjects.Text;
  private nextDistrictText!: Phaser.GameObjects.Text;
  private choiceViews: ChoiceView[] = [];
  private buttons: ArtButton[] = [];
  private restored = false;
  private leaving = false;
  private suspended = false;
  private reducedMotion = false;
  private renderScale = 1;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: MAIN_IDEA_MEADOW_SCENE_KEY }); }

  create() {
    this.choiceViews = [];
    this.buttons = [];
    this.leaving = false;
    this.suspended = false;

    const world = getStoryForestState();
    const missing = getMissingMainIdeaMeadowAssets(this.textures);
    if (!world.unlockedDistricts.includes(STORY_FOREST_DISTRICTS.mainIdeaMeadow)
      || !canEnterMainIdeaMeadow(this.textures)) {
      if (missing.length > 0) {
        console.error('[MainIdeaMeadow] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('SequenceStream');
      return;
    }

    void audio.enterSoundscape('story-forest');
    this.renderScale = this.configureRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.restored = world.completedDistricts.includes(STORY_FOREST_DISTRICTS.mainIdeaMeadow);
    this.restoreProgress(world.districtProgress[STORY_FOREST_DISTRICTS.mainIdeaMeadow]);
    visitStoryForestDistrict(STORY_FOREST_DISTRICTS.mainIdeaMeadow);
    SaveManager.setLastScene(MAIN_IDEA_MEADOW_SCENE_KEY);
    SaveManager.save();
    this.buildEnvironment();
    this.buildInterface();
    this.registerInput();
    this.registerLifecycle();
    this.refresh();
    this.applyTextResolution();
    this.cameras.main.fadeIn(this.reducedMotion ? 150 : 440, 118, 164, 74);
  }

  private restoreProgress(value: unknown) {
    const progress = value as Partial<MainIdeaMeadowProgress> | undefined;
    if (progress?.version === 1) {
      this.state = new ReadingEvidenceState(MAIN_IDEA_MEADOW_PASSAGE, progress);
      this.restored = progress.restored === true;
      return;
    }
    this.state = new ReadingEvidenceState(MAIN_IDEA_MEADOW_PASSAGE);
  }

  private buildEnvironment() {
    this.dormantBackground = this.add.image(640, 360, MAIN_IDEA_MEADOW_ASSETS.dormant.key)
      .setDisplaySize(1280, 720).setDepth(0);
    this.restoredBackground = this.add.image(640, 360, MAIN_IDEA_MEADOW_ASSETS.restored.key)
      .setDisplaySize(1280, 720).setDepth(1);
    this.storyPanel = this.add.image(350, 220, STORY_FOREST_ASSETS.storyMissionPanel.key).setDepth(30);
    this.cosmo = this.add.image(100, 650, STORY_FOREST_ASSETS.storyForestCosmo.key).setDepth(105);
  }

  private buildInterface() {
    this.titleText = this.makeText('MAIN IDEA MEADOW', 29, '#fff2b8', 'bold')
      .setStroke('#173f2b', 7).setDepth(110);
    this.storyTitle = this.makeText(MAIN_IDEA_MEADOW_PASSAGE.title, 21, '#4f3322', 'bold').setDepth(40);
    this.passageText = this.makeText('', 17, '#3e3028', 'normal')
      .setOrigin(0, 0).setAlign('left').setLineSpacing(6).setDepth(40);
    this.pageText = this.makeText('', 12, '#6e4a2d', 'bold').setDepth(40);
    this.previousText = this.makeText('← PREVIOUS', 13, '#5d3a22', 'bold')
      .setInteractive({ useHandCursor: true }).setDepth(42);
    this.nextPageText = this.makeText('NEXT →', 13, '#5d3a22', 'bold')
      .setInteractive({ useHandCursor: true }).setDepth(42);
    this.questionText = this.makeText(MAIN_IDEA_MEADOW_PASSAGE.question, 17, '#fff8dc', 'bold')
      .setStroke('#173f2b', 5).setWordWrapWidth(650).setDepth(110);
    this.phaseText = this.makeText('', 14, '#fff2b8', 'bold')
      .setStroke('#173f2b', 5).setDepth(110);
    this.feedbackText = this.makeText('', 13, '#fff8dc', 'bold')
      .setStroke('#173f2b', 5).setWordWrapWidth(720).setDepth(110);
    this.controlsText = this.makeText('←/→ pages • 1–4 choose • Enter check • H hint • R reset • Esc stream • M Home', 10, '#fff8dc', 'bold')
      .setStroke('#173f2b', 5).setDepth(110);
    this.backText = this.makeText('←  SEQUENCE STREAM', 14, '#ffffff', 'bold')
      .setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.homeText = this.makeText('HOME HUB  (M)', 14, '#ffffff', 'bold')
      .setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.nextDistrictText = this.makeText('INFERENCE HOLLOW • NEXT PATH', 15, '#fff2b8', 'bold')
      .setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    [this.previousText, this.nextPageText, this.backText, this.homeText, this.nextDistrictText]
      .forEach(target => ensureMinimumTextTouchTarget(target));
    this.previousText.on('pointerup', () => this.changePage(-1));
    this.nextPageText.on('pointerup', () => this.changePage(1));
    this.backText.on('pointerup', this.returnToStream, this);
    this.homeText.on('pointerup', this.returnHome, this);
    this.nextDistrictText.on('pointerup', this.tryNextDistrict, this);

    this.choiceViews = Array.from({ length: 4 }, (_unused, index) => {
      const image = this.add.image(0, 0, MAIN_IDEA_MEADOW_ASSETS.detailBloom.key)
        .setDepth(60).setInteractive({ useHandCursor: true });
      const label = this.makeText('', 12, '#3e3028', 'bold').setWordWrapWidth(140).setDepth(65);
      const badge = this.makeText(`${index + 1}`, 13, '#fff8dc', 'bold')
        .setStroke('#173f2b', 4).setDepth(66);
      image.on('pointerup', () => this.activateChoice(index));
      return { id: '', image, label, badge };
    });
    this.buttons = [
      this.makeButton('CHECK', () => this.check()),
      this.makeButton('HINT', () => this.hint()),
      this.makeButton('RESET', () => this.reset()),
    ];
  }

  private makeText(value: string, size: number, color: string, style: 'normal' | 'bold') {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_READING, fontSize: `${size}px`, fontStyle: style,
      color, align: 'center', lineSpacing: 3,
    }).setOrigin(0.5);
  }

  private makeButton(label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, CHARACTER_GROVE_ASSETS.connectButton.key)
      .setDepth(100).setInteractive({ useHandCursor: true });
    const labelText = this.makeText(label, 13, '#fff8dc', 'bold').setStroke('#173f2b', 4).setDepth(101);
    image.on('pointerup', action);
    return { image, label: labelText };
  }

  private changePage(delta: number) {
    if (!this.canAct()) return;
    const next = Phaser.Math.Clamp(this.state.snapshot.pageIndex + delta, 0, MAIN_IDEA_MEADOW_PASSAGE.pages.length - 1);
    if (!this.state.setPage(next)) return;
    this.feedbackText.setText('Notice which details explain the whole topic, not just one page.');
    audio.click();
    this.capture();
  }

  private activateChoice(index: number) {
    if (!this.canAct()) return;
    const snapshot = this.state.snapshot;
    if (!snapshot.allPagesRead || !snapshot.selectedClaimId) {
      const choice = MAIN_IDEA_MEADOW_PASSAGE.claimChoices[index];
      if (!choice || !this.state.selectClaim(choice.id)) return;
      this.feedbackText.setText('Main idea chosen. Now connect exactly two details that best support it.');
    } else {
      const detail = MAIN_IDEA_MEADOW_PASSAGE.evidenceDetails[index];
      if (!detail || !this.state.toggleEvidence(detail.id)) return;
      this.feedbackText.setText('Detail connection revised. Choose exactly two strong details, then check.');
    }
    audio.click();
    this.capture();
  }

  private check() {
    if (this.restored) return this.replay();
    if (!this.canAct()) return;
    const result = this.state.check();
    if (result !== 'complete') {
      const messages = {
        'not-read': 'Visit every page first. The main idea must fit the whole passage.',
        incomplete: 'Choose one main idea and exactly two supporting details.',
        'claim-mismatch': 'That idea does not fit every page. Reset the choices and try a broader idea.',
        'evidence-mismatch': 'That detail pair does not best prove the idea. Revise the flower connections.',
      } as const;
      this.feedbackText.setText(messages[result]);
      SaveManager.recordAnswer(MAIN_IDEA_MEADOW_PASSAGE.skillId, false);
      audio.wrong();
      return this.capture();
    }
    SaveManager.recordAnswer(MAIN_IDEA_MEADOW_PASSAGE.skillId, true);
    this.restored = true;
    completeStoryForestDistrictProgress(
      STORY_FOREST_DISTRICTS.mainIdeaMeadow,
      this.progressSnapshot(),
      MAIN_IDEA_MEADOW_PASSAGE.completionWorldEvent,
      STORY_FOREST_DISTRICTS.inferenceHollow,
    );
    this.feedbackText.setText(MAIN_IDEA_MEADOW_PASSAGE.successMessage);
    this.playRestoration();
    audio.restoration();
    this.capture();
  }

  private hint() {
    if (!this.canAct()) return;
    this.feedbackText.setText(this.state.requestHint());
    audio.hint();
    this.capture();
  }

  private reset() {
    if (!this.canAct() || !this.state.resetSelections()) return;
    this.feedbackText.setText('The idea and detail flowers are ready for a new connection. Read pages stay visited.');
    audio.click();
    this.capture();
  }

  private replay() {
    this.restored = false;
    this.state = new ReadingEvidenceState(MAIN_IDEA_MEADOW_PASSAGE);
    this.feedbackText.setText('Replay mode: rebuild the main-idea bloom without changing saved mastery.');
    audio.click();
    this.capture();
  }

  private capture() {
    saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.mainIdeaMeadow, this.progressSnapshot());
    this.refresh();
  }

  private progressSnapshot(): MainIdeaMeadowProgress {
    return { ...this.state.serialize(), restored: this.restored };
  }

  private refresh() {
    const snapshot = this.state.snapshot;
    this.restoredBackground.setAlpha(this.restored ? 1 : 0);
    this.dormantBackground.setAlpha(this.restored ? 0 : 1);
    this.storyTitle.setText(this.restored ? 'THE MEADOW REMEMBERS' : MAIN_IDEA_MEADOW_PASSAGE.title);
    this.passageText.setText(this.restored
      ? 'The supporting details feed one clear main idea. The vine path to Inference Hollow is open.'
      : MAIN_IDEA_MEADOW_PASSAGE.pages[snapshot.pageIndex]);
    this.pageText.setText(this.restored ? 'PASSAGE COMPLETE' : `PAGE ${snapshot.pageIndex + 1} OF ${snapshot.pageCount}`);
    this.previousText.setVisible(!this.restored && snapshot.pageIndex > 0);
    this.nextPageText.setVisible(!this.restored && snapshot.pageIndex < snapshot.pageCount - 1);
    const choosingClaim = snapshot.allPagesRead && !snapshot.selectedClaimId;
    const choosingEvidence = snapshot.allPagesRead && !!snapshot.selectedClaimId;
    this.phaseText.setText(this.restored
      ? 'MEADOW RESTORED'
      : !snapshot.allPagesRead
        ? `READ EVERY PAGE • ${snapshot.visitedPageIndexes.length}/${snapshot.pageCount} visited`
        : choosingClaim
          ? 'CHOOSE THE MAIN IDEA'
          : `CONNECT TWO DETAILS • ${snapshot.selectedEvidenceIds.length}/2 selected`);
    const choices = choosingClaim
      ? MAIN_IDEA_MEADOW_PASSAGE.claimChoices
      : choosingEvidence
        ? MAIN_IDEA_MEADOW_PASSAGE.evidenceDetails
        : [];
    this.choiceViews.forEach((view, index) => {
      const choice = choices[index];
      view.id = choice?.id ?? '';
      const selected = !!choice && (choosingClaim
        ? snapshot.selectedClaimId === choice.id
        : snapshot.selectedEvidenceIds.includes(choice.id));
      view.image.setVisible(!this.restored && !!choice).setAlpha(selected ? 1 : 0.86);
      view.label.setVisible(!this.restored && !!choice)
        .setText(choice ? `${choice.text}${selected ? '\nSELECTED' : ''}` : '');
      view.badge.setVisible(!this.restored && !!choice);
    });
    this.questionText.setVisible(!this.restored && snapshot.allPagesRead)
      .setText(choosingEvidence
        ? `Chosen idea: ${MAIN_IDEA_MEADOW_PASSAGE.claimChoices.find(choice => choice.id === snapshot.selectedClaimId)?.text}`
        : MAIN_IDEA_MEADOW_PASSAGE.question);
    this.buttons.forEach((button, index) => {
      const visible = !this.restored || index === 0;
      button.image.setVisible(visible);
      button.label.setVisible(visible);
    });
    this.buttons[0].label.setText(this.restored ? 'REPLAY' : 'CHECK');
    this.controlsText.setVisible(!this.restored);
    this.nextDistrictText.setVisible(this.restored);
    if (!this.feedbackText.text) this.feedbackText.setText('Read all three pages before choosing the idea that fits them together.');
    this.layout();
  }

  private layout() {
    const { safe, compact } = resolveStoryForestLayout(this);
    const portrait = safe.width < 700;
    const panelW = portrait ? safe.width - 26 : compact ? 430 : 480;
    const panelH = panelW * (1086 / 1448);
    const panelX = portrait ? safe.centerX : safe.x + panelW * 0.52 + 18;
    const panelY = safe.y + (portrait ? 190 : compact ? 190 : 215);
    this.storyPanel.setPosition(panelX, panelY).setDisplaySize(panelW, panelH);
    this.titleText.setPosition(safe.centerX, safe.y + 28).setFontSize(portrait ? 21 : compact ? 24 : 29);
    this.backText.setPosition(safe.x + (portrait ? 75 : 91), safe.y + 24).setFontSize(portrait ? 8 : compact ? 11 : 14);
    this.homeText.setPosition(safe.right - (portrait ? 55 : 67), safe.y + 24).setFontSize(portrait ? 9 : compact ? 11 : 14);
    this.storyTitle.setPosition(panelX, panelY - panelH * 0.34).setFontSize(portrait ? 17 : 21);
    this.passageText.setPosition(panelX - panelW * 0.35, panelY - panelH * 0.2)
      .setFontSize(portrait ? 13 : compact ? 15 : 17).setWordWrapWidth(panelW * 0.7);
    this.pageText.setPosition(panelX, panelY + panelH * 0.31);
    this.previousText.setPosition(panelX - panelW * 0.24, panelY + panelH * 0.39);
    this.nextPageText.setPosition(panelX + panelW * 0.24, panelY + panelH * 0.39);

    const activityX = portrait ? safe.centerX : safe.x + panelW + (safe.width - panelW) * 0.52;
    this.phaseText.setPosition(activityX, safe.y + (portrait ? 380 : compact ? 95 : 110))
      .setFontSize(portrait ? 12 : 14);
    this.questionText.setPosition(activityX, safe.y + (portrait ? 408 : compact ? 135 : 152))
      .setFontSize(portrait ? 12 : compact ? 14 : 17)
      .setWordWrapWidth(portrait ? safe.width - 40 : Math.max(360, safe.width - panelW - 80));
    const columns = portrait ? 2 : 2;
    const cardW = portrait ? Math.min(154, (safe.width - 50) / 2) : compact ? 185 : 205;
    const cardH = cardW;
    const gapX = cardW + (portrait ? 10 : 15);
    const gapY = portrait ? 112 : compact ? 150 : 168;
    const cardStartY = safe.y + (portrait ? 475 : compact ? 235 : 260);
    this.choiceViews.forEach((view, index) => {
      const row = Math.floor(index / columns);
      const column = index % columns;
      const x = activityX + (column - 0.5) * gapX;
      const y = cardStartY + row * gapY;
      view.image.setPosition(x, y).setDisplaySize(cardW, cardH);
      view.label.setPosition(x, y - cardH * 0.02)
        .setFontSize(portrait ? 8 : compact ? 9 : 11).setWordWrapWidth(cardW * 0.55);
      view.badge.setPosition(x - cardW * 0.34, y - cardH * 0.34).setFontSize(portrait ? 9 : 11);
    });
    const buttonY = safe.bottom - 48;
    const buttonGap = portrait ? Math.min(145, (safe.width - 80) / 2) : 165;
    this.buttons.forEach((button, index) => {
      const x = activityX + (index - 1) * buttonGap;
      button.image.setPosition(x, buttonY).setDisplaySize(portrait ? 120 : 140, portrait ? 54 : 62);
      button.label.setPosition(x, buttonY).setFontSize(portrait ? 10 : 13);
    });
    this.feedbackText.setPosition(activityX, safe.bottom - 102)
      .setFontSize(portrait ? 9 : compact ? 11 : 13)
      .setWordWrapWidth(portrait ? safe.width - 36 : Math.max(380, safe.width - panelW - 80));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 13).setFontSize(portrait ? 7 : compact ? 9 : 10);
    this.nextDistrictText.setPosition(activityX, safe.bottom - 100).setFontSize(portrait ? 12 : 15);
    this.cosmo.setPosition(safe.x + (compact ? 50 : 66), safe.bottom - (compact ? 49 : 59))
      .setDisplaySize(compact ? 73 : 90, compact ? 97 : 120);
  }

  private playRestoration() {
    const effect = this.add.image(720, 390, STORY_FOREST_ASSETS.storyMagicVfx.key)
      .setDisplaySize(560, 560).setDepth(95).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.68 : 0);
    if (this.reducedMotion) this.time.delayedCall(800, () => effect.destroy());
    else this.tweens.add({ targets: effect, alpha: 0.92, scaleX: 1.14, scaleY: 1.14,
      duration: 560, yoyo: true, hold: 160, onComplete: () => effect.destroy() });
  }

  private registerInput() { this.input.keyboard?.on('keydown', this.handleKeyDown, this); }

  private handleKeyDown(event: KeyboardEvent) {
    if (this.leaving || this.suspended) return;
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnToStream();
    if (key === 'm') return this.returnHome();
    if (key === 'arrowleft') return this.changePage(-1);
    if (key === 'arrowright') return this.changePage(1);
    if (key === 'enter' || key === ' ') return this.check();
    const choiceIndex = ['1', '2', '3', '4'].indexOf(key);
    if (choiceIndex >= 0) return this.activateChoice(choiceIndex);
    if (key === 'h') return this.hint();
    if (key === 'r') this.reset();
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.suspended = true; },
      onResume: () => { this.suspended = false; },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private canAct() { return !this.leaving && !this.suspended && !this.restored; }

  private configureRenderer() {
    const parentW = this.scale.parentSize.width || window.innerWidth;
    const parentH = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.min(parentW / STORY_FOREST_LAYOUT_WIDTH, parentH / STORY_FOREST_LAYOUT_HEIGHT);
    const scale = Phaser.Math.Clamp(cssScale * Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2), 1, 2);
    this.scale.scaleMode = Phaser.Scale.FIT;
    this.scale.autoCenter = Phaser.Scale.CENTER_BOTH;
    this.scale.displaySize.setAspectMode(Phaser.Scale.FIT);
    this.scale.setGameSize(Math.round(STORY_FOREST_LAYOUT_WIDTH * scale), Math.round(STORY_FOREST_LAYOUT_HEIGHT * scale));
    this.scale.refresh();
    this.cameras.main.setZoom(scale).centerOn(640, 360);
    return scale;
  }

  private applyTextResolution() {
    this.children.list.forEach(object => {
      if (object instanceof Phaser.GameObjects.Text) object.setResolution(this.renderScale);
    });
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      this.renderScale = this.configureRenderer();
      this.layout();
      this.applyTextResolution();
    }, 80);
  };

  private readonly returnToStream = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.mainIdeaMeadow, this.progressSnapshot());
    this.scene.start('SequenceStream');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.mainIdeaMeadow, this.progressSnapshot());
    SaveManager.setLastScene(MAIN_IDEA_MEADOW_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly tryNextDistrict = () => {
    if (!this.restored) return;
    if (!canEnterInferenceHollow(this.textures)) {
      this.feedbackText.setText('The hollow path is unlocked. Inference Hollow is awaiting its runtime QA gate.');
      return audio.click();
    }
    this.leaving = true;
    saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.mainIdeaMeadow, this.progressSnapshot());
    this.scene.start('InferenceHollow');
  };

  private shutdown() {
    if (this.state) saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.mainIdeaMeadow, this.progressSnapshot());
    this.unsubscribeLifecycle?.();
    audio.leaveSoundscape({ id: 'story-forest' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.tweens.killAll();
  }
}
